import { createSlice } from '@reduxjs/toolkit';
import {
  listSocialPosts,
  listSocialCommentsByPost,
  replySocialComment,
  setSocialCommentHidden,
  deleteSocialComment,
  syncSocialComments
} from 'src/actions/v2/meta/v2_actionSocialComments';

// ----------------------------------------------------------------------

const initialState = {
  error: null,
  // aba principal da sidebar do chat: 'conversas' | 'comentarios'
  activeChatTab: 'conversas',
  // lista mesclada de posts FB + IG, ordenada pelo comentario mais recente
  posts: [],
  loadingPosts: false,
  selectedPost: null,
  commentsByPost: {},
  loadingComments: false,
  sendingReply: false,
  actioningCommentId: null,
  syncing: false,
  // Comentarios que chegaram por socket e o atendente ainda nao viu (badge da aba)
  unreadComments: 0,
  unreadByPost: {},
  // Ultimos comment_ids processados via socket — evita contar duas vezes quando o
  // mesmo evento chega repetido (reconexao do ws, webhook reentregue pela Meta).
  socketSeenIds: []
};

const LIMITE_IDS_VISTOS = 50;

const slice = createSlice({
  name: 'socialComments',
  initialState,
  reducers: {
    hasError(state, action) {
      state.error = action.payload;
    },

    setActiveChatTab(state, action) {
      state.activeChatTab = action.payload;
      state.selectedPost = null;
      // Entrar na aba ja e "ver" os comentarios pendentes — zera o badge.
      if (action.payload === 'comentarios') state.unreadComments = 0;
    },

    setSelectedPost(state, action) {
      state.selectedPost = action.payload;
      const postId = action.payload?.post_id;
      if (postId && state.unreadByPost[postId]) {
        state.unreadComments = Math.max(0, state.unreadComments - state.unreadByPost[postId]);
        delete state.unreadByPost[postId];
      }
    },

    // Usado quando o atendente volta para a aba/lista sem abrir um post especifico
    clearUnreadComments(state) {
      state.unreadComments = 0;
      state.unreadByPost = {};
    },

    startLoadingPosts(state) {
      state.loadingPosts = true;
    },

    getPostsSuccess(state, action) {
      state.posts = action.payload.data;
      state.loadingPosts = false;
    },

    stopLoadingPosts(state) {
      state.loadingPosts = false;
    },

    startLoadingComments(state) {
      state.loadingComments = true;
    },

    getCommentsSuccess(state, action) {
      const { postId, post, comments } = action.payload;
      state.commentsByPost[postId] = comments;
      state.loadingComments = false;
      // Hidrata o post selecionado com os metadados do banco (caption/midia/permalink)
      if (post && state.selectedPost?.post_id === postId) {
        state.selectedPost = { ...state.selectedPost, ...post };
      }
    },

    stopLoadingComments(state) {
      state.loadingComments = false;
    },

    setSendingReply(state, action) {
      state.sendingReply = action.payload;
    },

    setActioningComment(state, action) {
      state.actioningCommentId = action.payload;
    },

    appendComment(state, action) {
      const { postId, comment } = action.payload;
      const lista = state.commentsByPost[postId];
      if (lista && !lista.some((c) => c.comment_id === comment.comment_id)) {
        lista.push(comment);
      }
    },

    // Novo comentario chegando via socket (lais-webhook -> ws -> front)
    receiveSocketComment(state, action) {
      const { platform, post_id, canal_id, comment } = action.payload || {};
      if (!platform || !post_id) return;

      const commentId = comment?.comment_id;
      if (commentId) {
        if (state.socketSeenIds.includes(commentId)) return;
        state.socketSeenIds.push(commentId);
        if (state.socketSeenIds.length > LIMITE_IDS_VISTOS) state.socketSeenIds.shift();
      }

      // Nao lido enquanto o atendente nao estiver olhando exatamente este post.
      const olhandoOPost = state.activeChatTab === 'comentarios' && state.selectedPost?.post_id === post_id;
      if (!olhandoOPost) {
        state.unreadComments += 1;
        state.unreadByPost[post_id] = (state.unreadByPost[post_id] || 0) + 1;
      }

      const lastCommentPreview = comment
        ? { from: comment.from, text: comment.text, isMe: !!comment.isMe }
        : undefined;

      const idx = state.posts.findIndex((p) => p.post_id === post_id);
      if (idx >= 0) {
        const post = {
          ...state.posts[idx],
          comments_count: (state.posts[idx].comments_count || 0) + 1,
          unreplied: (state.posts[idx].unreplied || 0) + 1,
          last_comment_at: comment?.comment_created_time || new Date().toISOString(),
          ...(lastCommentPreview ? { last_comment: lastCommentPreview } : {})
        };
        state.posts.splice(idx, 1);
        state.posts.unshift(post);
      } else {
        state.posts.unshift({
          post_id,
          platform,
          canal_id,
          comments_count: 1,
          unreplied: 1,
          last_comment_at: comment?.comment_created_time || new Date().toISOString(),
          last_comment: lastCommentPreview
        });
      }

      const thread = state.commentsByPost[post_id];
      if (thread && comment && !thread.some((c) => c.comment_id === comment.comment_id)) {
        thread.push(comment);
      }
    },

    // Atualizacao de comentario (hide/unhide local, edited/remove via socket)
    patchComment(state, action) {
      const { post_id, comment_id, type, payload } = action.payload || {};
      const thread = state.commentsByPost[post_id];
      if (!thread) return;

      if (type === 'remove') {
        state.commentsByPost[post_id] = thread.filter(
          (c) => c.comment_id !== comment_id && c.parent_id !== comment_id
        );
        return;
      }

      const comment = thread.find((c) => c.comment_id === comment_id);
      if (!comment) return;
      if (type === 'edited') comment.text = payload?.text;
      if (type === 'hidden') comment.is_hidden = payload?.is_hidden;
    },

    setSyncing(state, action) {
      state.syncing = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setActiveChatTab,
  setSelectedPost,
  clearUnreadComments,
  appendComment,
  receiveSocketComment,
  patchComment
} = slice.actions;

// ----------------------------------------------------------------------

export function getSocialPosts(clienteId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingPosts());
    try {
      const response = await listSocialPosts(clienteId);
      dispatch(slice.actions.getPostsSuccess({ data: response.data.data }));
    } catch (error) {
      dispatch(slice.actions.stopLoadingPosts());
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSocialComments(clienteId, platform, postId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingComments());
    try {
      const response = await listSocialCommentsByPost(clienteId, platform, postId);
      const { post, comments } = response.data.data;
      dispatch(slice.actions.getCommentsSuccess({ postId, post, comments }));
    } catch (error) {
      dispatch(slice.actions.stopLoadingComments());
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function replyToComment(payload) {
  return async (dispatch) => {
    dispatch(slice.actions.setSendingReply(true));
    try {
      const response = await replySocialComment(payload);
      const doc = response.data.data;
      if (doc) dispatch(slice.actions.appendComment({ postId: payload.post_id, comment: doc }));
      dispatch(slice.actions.setSendingReply(false));
      return { success: true };
    } catch (error) {
      dispatch(slice.actions.setSendingReply(false));
      dispatch(slice.actions.hasError(error));
      return { success: false, message: error?.response?.data?.mensagem };
    }
  };
}

export function toggleHideComment(payload) {
  return async (dispatch) => {
    dispatch(slice.actions.setActioningComment(payload.comment_id));
    try {
      await setSocialCommentHidden(payload);
      dispatch(slice.actions.patchComment({
        post_id: payload.post_id,
        comment_id: payload.comment_id,
        type: 'hidden',
        payload: { is_hidden: payload.hidden }
      }));
      dispatch(slice.actions.setActioningComment(null));
      return { success: true };
    } catch (error) {
      dispatch(slice.actions.setActioningComment(null));
      dispatch(slice.actions.hasError(error));
      return { success: false, message: error?.response?.data?.mensagem };
    }
  };
}

export function removeSocialComment(payload) {
  return async (dispatch) => {
    dispatch(slice.actions.setActioningComment(payload.comment_id));
    try {
      await deleteSocialComment(payload);
      dispatch(slice.actions.patchComment({
        post_id: payload.post_id,
        comment_id: payload.comment_id,
        type: 'remove'
      }));
      dispatch(slice.actions.setActioningComment(null));
      return { success: true };
    } catch (error) {
      dispatch(slice.actions.setActioningComment(null));
      dispatch(slice.actions.hasError(error));
      return { success: false, message: error?.response?.data?.mensagem };
    }
  };
}

// O backend descobre sozinho os canais FB/IG do cliente (incluindo os das
// sub-contas em multi-session) e dispara o backfill de cada um.
export function syncSocialCommentsThunk({ cliente_id }) {
  return async (dispatch) => {
    dispatch(slice.actions.setSyncing(true));
    try {
      await syncSocialComments({ cliente_id });
      // O sync roda fire-and-forget no backend — aguarda um pouco e refaz a listagem.
      await new Promise((resolve) => setTimeout(resolve, 4000));
      const response = await listSocialPosts(cliente_id);
      dispatch(slice.actions.getPostsSuccess({ data: response.data.data }));
      dispatch(slice.actions.setSyncing(false));
      return { success: true };
    } catch (error) {
      dispatch(slice.actions.setSyncing(false));
      dispatch(slice.actions.hasError(error));
      return { success: false };
    }
  };
}
